import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { Box, Button } from '@mui/material';
import classnames from 'classnames';
import _ from 'lodash';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import auth from '../../../components/auth';
import { CommunityPostCell } from './CommunityPostCell';
import { useCommunityPosts } from '../hooks/useCommunityPosts';
import { PostSkeleton } from '../components/PostSkeleton';
import { LoadMorePosts } from '../components/LoadMorePosts';
import AddPost from '../components/AddPost';
import { useDeleteCommunityPost } from '../hooks/useDeleteCommunityPost';
import { useToggleReactionPost } from '../hooks/useToggleReactionPost';
import { Post, useBlockUserMutation } from '../../../../generated/graphql';
import { useCommunityIndicator } from '../../hooks/use-community-indicator';
import { communityQueryKeys } from '../constants';
import { EmptyFeed } from './EmptyFeed';
import { mixTrack } from '@services/mixpanel';

type UserData = {
  name: string;
  picture: string;
  isOwner: boolean;
};
type Props = { influencerId: number; communityId: number; user: UserData };

export const CommunityFeed = ({ influencerId, communityId, user }: Props) => {
  const isAdminModerator = auth.isAdministrator() || auth.isModerator();
  const queryClient = useQueryClient();
  const deletePostMutation = useDeleteCommunityPost(communityId);
  const togglePostReaction = useToggleReactionPost(communityId);
  const [blockUserMutation] = useBlockUserMutation();
  const [communityData, setCommunityData] = useState<Post[]>([]);
  const [hasNewPosts, setHasNewPosts] = useState<boolean>(false);
  const [addPostVisible, setAddPostVisible] = useState<boolean>(true);
  const addPostRef = useRef<HTMLDivElement>(null);

  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    isInitialLoading,
    hasNextPage,
  } = useCommunityPosts(communityId);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setAddPostVisible(entry.isIntersecting);
      },
      { rootMargin: '-50px' },
    );

    if (addPostRef.current) {
      observer.observe(addPostRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasNewPosts]);

  useEffect(() => {
    const fetchedPosts = _.flatten(data?.pages ?? []);
    if (
      !_.isEmpty(communityData) &&
      !_.isEmpty(fetchedPosts) &&
      fetchedPosts[0]?.id !== communityData[0].id
    ) {
      setHasNewPosts(true);
    }
    if (_.isEmpty(communityData)) {
      const newData = data?.pages ? _.flatten(data.pages) : [];
      setCommunityData(newData as Post[]);
    }
  }, [data]);

  useEffect(() => {
    const posts = data?.pages[0] ?? [];
    const includesConsumerPost = posts.some(
      (post) => post.author.id !== influencerId,
    );
    const includesCreatorPost = posts.some(
      (post) => post.author.id === influencerId,
    );

    mixTrack({
      type: 'Community Viewed',
      payload: {
        'Includes a consumer post': String(includesConsumerPost),
        'Includes a creator post': String(includesCreatorPost),
      },
    });
  }, [data]);

  const { setLastSeenPostId } = useCommunityIndicator(true);
  if (!isAdminModerator && data?.pages && !_.isEmpty(data.pages)) {
    const page = data.pages[0];
    page && !_.isEmpty(page) && setLastSeenPostId(page[0].id);
  }

  const onBlock = async (communityId: number, userId: number) => {
    return blockUserMutation({
      variables: {
        communityId,
        userId,
      },
    })
      .then(() => {
        toast.success('User blocked successfully');
        queryClient.refetchQueries(communityQueryKeys.posts(+communityId));
      })
      .catch(() => {
        toast.error('Something went wrong with blocking user');
      });
  };

  const onLoadNewPosts = () => {
    const fetchedPosts = _.flatten(data?.pages ?? []);
    setCommunityData(fetchedPosts as Post[]);
    setHasNewPosts(false);
    scrollToTop();
  };

  const scrollToTop = () => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };

  const hasPosts = _.flatten(data?.pages ?? []).length > 0;

  return (
    <Box>
      <Box className="bg-white md:p-[30px] rounded-2xl">
        <Box ref={addPostRef}>
          <AddPost
            communityId={communityId}
            ownerProfilePicture={user.picture}
            ownerName={user.name}
          />
        </Box>
        {isInitialLoading ? (
          <PostSkeleton />
        ) : (
          data &&
          hasPosts && (
            <Box className="relative">
              <Box
                className={classnames(
                  'w-full flex justify-between z-10000 top-2.5',
                  {
                    absolute: addPostVisible,
                    'fixed right-4 lg:pl-[280px]': !addPostVisible,
                  },
                )}
              >
                {!addPostVisible && <Box className="w-[100px]" />}
                {hasNewPosts && (
                  <Button
                    variant="contained"
                    className={classnames(
                      'rounded-[100px] w-[145px] h-[28px] m-auto',
                    )}
                    startIcon={
                      <FontAwesomeIcon
                        icon={faArrowUp}
                        className="pl-2.5 h-4"
                      />
                    }
                    onClick={onLoadNewPosts}
                  >
                    <span className="px-2">New Posts</span>
                  </Button>
                )}
                {!addPostVisible && (
                  <Button
                    variant="contained"
                    className="rounded-[100px] w-[80px] h-[28px]"
                    startIcon={
                      <FontAwesomeIcon
                        icon={faArrowUp}
                        className="pl-2.5 h-4"
                      />
                    }
                    onClick={scrollToTop}
                  >
                    Top
                  </Button>
                )}
              </Box>
              {communityData.map((post) => (
                <CommunityPostCell
                  influencerId={influencerId}
                  communityId={communityId}
                  isUserCommunityOwner={user.isOwner}
                  postData={post}
                  key={post.id}
                  onDelete={() => deletePostMutation.mutate(post.id)}
                  onBlock={onBlock}
                  onToggleReaction={(type) =>
                    togglePostReaction.mutateAsync({
                      type,
                      postId: post.id,
                    })
                  }
                />
              ))}
            </Box>
          )
        )}
        {!isInitialLoading && hasPosts && hasNextPage && (
          <LoadMorePosts
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        )}
      </Box>
      {!isInitialLoading && !hasPosts && <EmptyFeed />}
    </Box>
  );
};
